import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, Plus, Trash2, Send, Download, Loader2, 
  DollarSign, Calculator, CheckCircle, Clock, X 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_OPTIONS = ["each", "hour", "sqft", "linear ft", "gallon", "day", "job"];

const QuoteBuilder = ({ workerId, onClose }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    job_description: "",
    items: [{ description: "", quantity: 1, unit_price: 0, unit: "each" }],
    notes: "",
    tax_rate: 0,
    valid_days: 30
  });

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/worker/${workerId}`);
      setQuotes(response.data);
    } catch (error) {
      console.error("Error fetching quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, unit_price: 0, unit: "each" }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const tax = subtotal * (parseFloat(formData.tax_rate) || 0) / 100;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.some(item => !item.description || !item.unit_price)) {
      toast.error("Please fill in all item details");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/quotes`, {
        worker_id: workerId,
        ...formData,
        items: formData.items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        })),
        tax_rate: parseFloat(formData.tax_rate) || 0
      });
      
      toast.success("Quote created!");
      setShowForm(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create quote");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      job_description: "",
      items: [{ description: "", quantity: 1, unit_price: 0, unit: "each" }],
      notes: "",
      tax_rate: 0,
      valid_days: 30
    });
  };

  const sendQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/send`);
      toast.success("Quote sent to customer!");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to send quote");
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!confirm("Delete this quote?")) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const { subtotal, tax, total } = calculateTotals();

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-700",
      sent: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      expired: "bg-orange-100 text-orange-700"
    };
    return styles[status] || styles.draft;
  };

  return (
    <div className="space-y-6" data-testid="quote-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium text-[#202124] flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#1a73e8]" />
          Quote Builder
        </h2>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
          data-testid="new-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Quote
        </Button>
      </div>

      {/* Quotes List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-12 bg-[#f8f9fa] rounded-xl">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-[#5f6368]">No quotes yet</p>
          <p className="text-sm text-[#80868b]">Create your first quote to send to customers</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map(quote => (
            <div 
              key={quote.id} 
              className="bg-white rounded-xl border border-[#dadce0] p-4 hover:shadow-sm transition-shadow"
              data-testid={`quote-${quote.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm text-[#5f6368]">{quote.quote_number}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>
                  <p className="font-medium text-[#202124]">{quote.customer_name}</p>
                  <p className="text-sm text-[#5f6368] line-clamp-1">{quote.job_description}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#202124]">${quote.total.toFixed(2)}</p>
                  <p className="text-xs text-[#5f6368]">
                    Valid until {new Date(quote.valid_until).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#f1f3f4]">
                {quote.status === "draft" && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => sendQuote(quote.id)}
                      className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-lg text-xs"
                      data-testid={`send-quote-${quote.id}`}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Send
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedQuote(quote)}
                      className="rounded-lg text-xs"
                    >
                      View
                    </Button>
                  </>
                )}
                {quote.status === "sent" && (
                  <span className="text-xs text-[#5f6368] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Awaiting response
                  </span>
                )}
                {quote.status === "accepted" && (
                  <span className="text-xs text-[#1e8e3e] flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Accepted
                  </span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteQuote(quote.id)}
                  className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Quote Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#1a73e8]" />
              Create New Quote
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="quote-customer-name"
                />
              </div>
              <div>
                <Label>Customer Email *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="quote-customer-email"
                />
              </div>
            </div>

            <div>
              <Label>Job Description *</Label>
              <Textarea
                value={formData.job_description}
                onChange={(e) => setFormData({...formData, job_description: e.target.value})}
                required
                rows={2}
                className="mt-1"
                data-testid="quote-job-description"
              />
            </div>

            {/* Line Items */}
            <div>
              <Label className="mb-2 block">Line Items</Label>
              <div className="space-y-2">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        className="text-sm"
                        data-testid={`item-description-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="text-sm"
                        data-testid={`item-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={item.unit}
                        onChange={(e) => updateItem(idx, "unit", e.target.value)}
                        className="w-full h-10 px-2 text-sm rounded-md border border-[#dadce0]"
                      >
                        {UNIT_OPTIONS.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                        className="text-sm"
                        data-testid={`item-price-${idx}`}
                      />
                    </div>
                    <div className="col-span-1 text-right font-medium text-sm">
                      ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                className="mt-2"
                data-testid="add-line-item-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Line
              </Button>
            </div>

            {/* Tax Rate */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({...formData, tax_rate: e.target.value})}
                  className="mt-1"
                  data-testid="quote-tax-rate"
                />
              </div>
              <div>
                <Label>Valid For (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.valid_days}
                  onChange={(e) => setFormData({...formData, valid_days: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
                placeholder="Terms, conditions, or additional notes..."
                className="mt-1"
              />
            </div>

            {/* Totals */}
            <div className="bg-[#f8f9fa] rounded-xl p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#5f6368]">Subtotal</span>
                  <span className="text-[#202124]">${subtotal.toFixed(2)}</span>
                </div>
                {parseFloat(formData.tax_rate) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5f6368]">Tax ({formData.tax_rate}%)</span>
                    <span className="text-[#202124]">${tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#dadce0]">
                  <span>Total</span>
                  <span className="text-[#1a73e8]">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#1a73e8] hover:bg-[#1557b0]"
                data-testid="create-quote-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Quote"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Quote Modal */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-mono text-sm text-[#5f6368]">{selectedQuote.quote_number}</p>
                  <p className="font-medium text-[#202124]">{selectedQuote.customer_name}</p>
                  <p className="text-sm text-[#5f6368]">{selectedQuote.customer_email}</p>
                </div>
                <span className={`px-3 py-1 text-sm rounded-full ${getStatusBadge(selectedQuote.status)}`}>
                  {selectedQuote.status}
                </span>
              </div>

              <div className="bg-[#f8f9fa] rounded-lg p-3">
                <p className="text-sm text-[#202124]">{selectedQuote.job_description}</p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fa]">
                    <tr>
                      <th className="text-left p-2">Item</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuote.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{item.description}</td>
                        <td className="text-right p-2">{item.quantity} {item.unit}</td>
                        <td className="text-right p-2">${item.unit_price}</td>
                        <td className="text-right p-2">${(item.quantity * item.unit_price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#f8f9fa]">
                    <tr className="border-t">
                      <td colSpan="3" className="text-right p-2 font-medium">Subtotal</td>
                      <td className="text-right p-2">${selectedQuote.subtotal.toFixed(2)}</td>
                    </tr>
                    {selectedQuote.tax_rate > 0 && (
                      <tr>
                        <td colSpan="3" className="text-right p-2">Tax ({selectedQuote.tax_rate}%)</td>
                        <td className="text-right p-2">${selectedQuote.tax_amount.toFixed(2)}</td>
                      </tr>
                    )}
                    <tr className="border-t">
                      <td colSpan="3" className="text-right p-2 font-bold">Total</td>
                      <td className="text-right p-2 font-bold text-[#1a73e8]">${selectedQuote.total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedQuote.notes && (
                <div className="text-sm text-[#5f6368]">
                  <strong>Notes:</strong> {selectedQuote.notes}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteBuilder;
